import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const coreSlide = createSlice({
    name: "core",
    initialState: {
        sidebarShow: true,
        commonData: null,
        urlParam: null,

        sendOtpSuccess: false,
        sendOtpFail: false,
        sendOtpErrorMessage: null,
        sendOtpResponseData: null,

        verifyOtpSuccess: false,
        verifyOtpFail: false,
        verifyOtpErrorMessage: null,
        verifyOtpResponseData: null,

        registerSuccess: false,
        registerFail: false,
        registerErrorMessage: null,
        registerResponseData: null,

        loggedIn: false,
        loginFailedDetail: null,
        loginSuccess: false,
        loginFail: false,
        logoutSuccess: false,
        logoutFail: false,

        // Refresh token state
        accessToken: localStorage.getItem('customer_accessToken') || null,
        refreshToken: localStorage.getItem('customer_refreshToken') || null,
        refreshTokenSuccess: false,
        refreshTokenFail: false,
        isRefreshing: false,

        insertSearchActivityLogSuccess: false,
        insertSearchActivityLogFail: false,

        // Credit Reload state
        processCreditReloadSuccess: false,
        processCreditReloadFail: false,
        processCreditReloadErrorMessage: null,
        processCreditReloadResponseData: null,

        // Update Profile
        updateProfileSuccess: false,
        updateProfileFail: false,
        updateProfileErrorMessage: null,

        // Update Password
        updatePasswordLoading: false,
        updatePasswordSuccess: false,
        updatePasswordFail: false,
        updatePasswordErrorMessage: null,
    },
    reducers: {
        setSideBarShow: (state, action) => {
            state.sidebarShow = action.payload;
        },
        setUrlParam: (state, action) => {
            state.urlParam = action.payload;
        },
        setCommonData: (state, action) => {
            state.commonData = action.payload.meta;
        },

        doSendOtpSuccess: (state, action) => {
            state.sendOtpSuccess = true;
            state.sendOtpFail = false;
            state.sendOtpErrorMessage = null;
            state.sendOtpResponseData = action.payload.data;
        },
        doSendOtpFail: (state, action) => {
            state.sendOtpSuccess = false;
            state.sendOtpFail = true;
            state.sendOtpErrorMessage = action.payload.data;
        },

        doVerifyOtpSuccess: (state, action) => {
            state.verifyOtpSuccess = true;
            state.verifyOtpFail = false;
            state.verifyOtpErrorMessage = null;
            state.verifyOtpResponseData = action.payload.data;
        },
        doVerifyOtpFail: (state, action) => {
            state.verifyOtpSuccess = false;
            state.verifyOtpFail = true;
            state.verifyOtpErrorMessage = action.payload.data;
        },

        doRegisterSuccess: (state, action) => {
            state.registerSuccess = true;
            state.registerFail = false;
            state.registerErrorMessage = null;
            state.registerResponseData = action.payload.data;
        },
        doRegisterFail: (state, action) => {
            state.registerSuccess = false;
            state.registerFail = true;
            state.registerErrorMessage = action.payload.data;
        },

        doLoginSuccess: (state, action) => {
            state.user = action.payload.data;
            state.loggedIn = null; // Clear stale loggedIn from previous session until doGetLoggedInUser completes
            state.loginSuccess = true;
            state.loginFail = false;
            state.logoutSuccess = false;
            state.loginFailedDetail = null;

            // Store tokens if they exist in the response
            const responseData = action.payload.data || action.payload;
            if (responseData.accessToken) {
                state.accessToken = responseData.accessToken;
                localStorage.setItem('customer_accessToken', responseData.accessToken);
            }
            if (responseData.refreshToken) {
                state.refreshToken = responseData.refreshToken;
                localStorage.setItem('customer_refreshToken', responseData.refreshToken);
            }
        },
        doLoginFail: (state, action) => {
            state.loggedIn = null;
            state.loginSuccess = false;
            state.loginFail = true;
            state.loginFailedDetail = action.payload.data;
        },

        doLoggedInSuccess: (state, action) => {
            state.loggedIn = action.payload?.data;
        },
        doLoggedInFail: (state, action) => {
            state.loggedIn = null;
        },

        doLogoutSuccess: (state, action) => {
            state.loggedIn = null;
            state.loginSuccess = false;
            state.logoutSuccess = true;
            state.logoutFail = false;
            // Clear tokens on logout
            state.accessToken = null;
            state.refreshToken = null;
            localStorage.removeItem('customer_accessToken');
            localStorage.removeItem('customer_refreshToken');
        },
        doLogoutFail: (state, action) => {
            state.logoutSuccess = false;
            state.logoutFail = true;
        },

        resetLogoutState: (state, action) => {
            state.logoutSuccess = action.payload;
            state.logoutFail = action.payload;
            state.urlParam = null;
        },
        resetRegisterState: (state, action) => {
            state.registerSuccess = action.payload;
            state.registerFail = action.payload;
            state.registerResponseData = null;
            state.registerErrorMessage = null;
        },
        resetLoginFail: (state, action) => {
            state.loginFailedDetail = null;
            state.loginSuccess = false;
            state.loginFail = false;
            state.logoutSuccess = false;
        },
        resetSendOtp: (state, action) => {
            state.sendOtpSuccess = action.payload;
            state.sendOtpFail = action.payload;
            state.sendOtpResponseData = null;
            state.sendOtpErrorMessage = null;
        },
        resetVerifyOtp: (state, action) => {
            state.verifyOtpSuccess = action.payload;
            state.verifyOtpFail = action.payload;
            state.verifyOtpResponseData = null;
            state.verifyOtpErrorMessage = null;
        },

        doInsertSearchActivityLogSuccess: (state, action) => {
            state.insertSearchActivityLogSuccess = true;
            state.insertSearchActivityLogFail = false;
        },
        doInsertSearchActivityLogFail: (state, action) => {
            state.insertSearchActivityLogSuccess = false;
            state.insertSearchActivityLogFail = true;
        },

        // Credit Reload Actions
        doProcessCreditReloadSuccess: (state, action) => {
            state.processCreditReloadSuccess = true;
            state.processCreditReloadFail = false;
            state.processCreditReloadErrorMessage = null;
            state.processCreditReloadResponseData = action.payload.data;
        },
        doProcessCreditReloadFail: (state, action) => {
            state.processCreditReloadSuccess = false;
            state.processCreditReloadFail = true;
            // payload is now a normalized flat object: { errorMessage }
            state.processCreditReloadErrorMessage = action.payload;
        },
        resetProcessCreditReload: (state) => {
            state.processCreditReloadSuccess = false;
            state.processCreditReloadFail = false;
            state.processCreditReloadErrorMessage = null;
            state.processCreditReloadResponseData = null;
        },

        // Update Profile Actions
        doUpdateProfileSuccess: (state, action) => {
            state.updateProfileSuccess = true;
            state.updateProfileFail = false;
            state.updateProfileErrorMessage = null;
        },
        doUpdateProfileFail: (state, action) => {
            state.updateProfileSuccess = false;
            state.updateProfileFail = true;
            state.updateProfileErrorMessage = action.payload.data;
        },
        resetUpdateProfile: (state) => {
            state.updateProfileSuccess = false;
            state.updateProfileFail = false;
            state.updateProfileErrorMessage = null;
        },

        // Update Password Actions
        doUpdatePasswordLoading: (state) => {
            state.updatePasswordLoading = true;
            state.updatePasswordSuccess = false;
            state.updatePasswordFail = false;
        },
        doUpdatePasswordSuccess: (state, action) => {
            state.updatePasswordLoading = false;
            state.updatePasswordSuccess = true;
            state.updatePasswordFail = false;
            state.updatePasswordErrorMessage = null;
        },
        doUpdatePasswordFail: (state, action) => {
            state.updatePasswordLoading = false;
            state.updatePasswordSuccess = false;
            state.updatePasswordFail = true;
            state.updatePasswordErrorMessage = action.payload.data;
        },
        resetUpdatePassword: (state) => {
            state.updatePasswordLoading = false;
            state.updatePasswordSuccess = false;
            state.updatePasswordFail = false;
            state.updatePasswordErrorMessage = null;
        },

        // Refresh Token Actions
        setTokens: (state, action) => {
            state.accessToken = action.payload.accessToken;
            state.refreshToken = action.payload.refreshToken;
            if (action.payload.accessToken) {
                localStorage.setItem('customer_accessToken', action.payload.accessToken);
            }
            if (action.payload.refreshToken) {
                localStorage.setItem('customer_refreshToken', action.payload.refreshToken);
            }
        },
        clearTokens: (state) => {
            state.loggedIn = null;
            state.loginSuccess = false;
            state.accessToken = null;
            state.refreshToken = null;
            localStorage.removeItem('customer_accessToken');
            localStorage.removeItem('customer_refreshToken');
        },
        setRefreshing: (state, action) => {
            state.isRefreshing = action.payload;
        },
        doRefreshTokenSuccess: (state, action) => {
            state.refreshTokenSuccess = true;
            state.refreshTokenFail = false;
            state.isRefreshing = false;
            state.accessToken = action.payload.accessToken;
            if (action.payload.refreshToken) {
                state.refreshToken = action.payload.refreshToken;
            }
            if (action.payload.accessToken) {
                localStorage.setItem('customer_accessToken', action.payload.accessToken);
            }
            if (action.payload.refreshToken) {
                localStorage.setItem('customer_refreshToken', action.payload.refreshToken);
            }
        },
        doRefreshTokenFail: (state) => {
            state.refreshTokenSuccess = false;
            state.refreshTokenFail = true;
            state.isRefreshing = false;
            state.accessToken = null;
            state.refreshToken = null;
            localStorage.removeItem('customer_accessToken');
            localStorage.removeItem('customer_refreshToken');
        },
    },
});

export const doLogin = (param) => async (dispatch) => {
    try {
        const url = `/api/customer/auth/login`;
        const response = await axios.post(url, param);

        dispatch(doLoginSuccess(response.data));
    } catch (error) {
        dispatch(doLoginFail(error.response));
    }
};

export const doGetCommonData = () => async (dispatch) => {
    try {
        const response = await axios.get("/api/customer/commons/meta");

        dispatch(setCommonData({ meta: response.data }));
    } catch (error) {
        dispatch(setCommonData({ meta: null }));
    }
};

export const doSendOtp = (param) => async (dispatch) => {
    try {
        const url = `/api/customer/auth/send-otp`;
        const response = await axios.post(url, param);

        dispatch(doSendOtpSuccess(response.data));
    } catch (error) {
        dispatch(doSendOtpFail(error.response));
    }
};

export const doVerifyOtp = (param) => async (dispatch) => {
    try {
        const url = `/api/customer/auth/verify-otp`;
        const response = await axios.post(url, param);

        dispatch(doVerifyOtpSuccess(response.data));
    } catch (error) {
        dispatch(doVerifyOtpFail(error.response));
    }
};

export const doRegister = (param) => async (dispatch) => {
    try {
        const url = `/api/customer/auth/register`;
        const response = await axios.post(url, param);

        dispatch(doRegisterSuccess(response.data));
    } catch (error) {
        dispatch(doRegisterFail(error.response));
    }
};

export const doRegisterGoogle = (param) => async (dispatch) => {
    try {
        const url = `/api/customer/auth/register/google`;
        const response = await axios.post(url, param);

        dispatch(doRegisterSuccess(response.data));
    } catch (error) {
        dispatch(doRegisterFail(error.response));
    }
};

export const doResetRegisterState = () => async (dispatch) => {
    dispatch(resetRegisterState(false));
};

export const doGetLoggedInUser = () => async (dispatch) => {
    try {
        const url = `/api/customer/auth/me`;
        const response = await axios.get(url);

        dispatch(doLoggedInSuccess(response.data));
    } catch (error) {
        dispatch(doLoggedInFail(error.response));
    }
};

export const doLogout = () => async (dispatch, getState) => {
    try {
        const state = getState();
        const refreshToken = state.core?.refreshToken;

        const url = `/api/customer/auth/logout`;
        const response = await axios.post(url, {
            refreshToken: refreshToken
        });

        dispatch(doLogoutSuccess(response.data));
    } catch (error) {
        dispatch(doLogoutFail(error.response));
    }
};

export const doResetLogoutState = () => async (dispatch) => {
    dispatch(resetLogoutState(false));
};

export const doResetLoginFail = () => async (dispatch) => {
    dispatch(resetLoginFail(false));
};

export const doResetSendOtp = () => async (dispatch) => {
    dispatch(resetSendOtp(false));
};

export const doResetVerifyOtp = () => async (dispatch) => {
    dispatch(resetVerifyOtp(false));
};

export const doSetUrlParam = (param) => async (dispatch) => {
    dispatch(setUrlParam(param));
};

export const updateSidebarShow = (param) => async (dispatch) => {
    dispatch(setSideBarShow(param));
};

export const doInsertSearchActivityLog = (param) => async (dispatch) => {
    try {
        const url = `/api/customer/search-activity-log`;
        const response = await axios.post(url, param);

        dispatch(doInsertSearchActivityLogSuccess(response.data));
    } catch (error) {
        dispatch(doInsertSearchActivityLogFail(error.response));
    }
};

// Process Credit Reload
export const doProcessCreditReload = (param) => async (dispatch) => {
    try {
        const url = `/api/customer/credit/reload`;
        const response = await axios.post(url, param);

        dispatch(doProcessCreditReloadSuccess(response.data));
    } catch (error) {
        // Normalize every backend error shape into one flat, serializable object.
        // The controller returns { data: { errorMessage } }; Joi-validation and
        // runtime errors come back flat as { errorMessage }; network/timeout errors
        // have no response at all. Never dispatch the raw axios response — it holds
        // non-serializable fields (config.adapter, request) that Redux warns about.
        const body = (error.response && error.response.data) || {};
        const errorMessage =
            (body.data && body.data.errorMessage) ||
            body.errorMessage ||
            "Network error. Please check your connection and try again.";
        dispatch(doProcessCreditReloadFail({ errorMessage }));
    }
};

export const doResetProcessCreditReload = () => async (dispatch) => {
    dispatch(resetProcessCreditReload());
};

export const doUpdateProfile = (param) => async (dispatch) => {
    try {
        const url = `/api/customer/auth/me`;
        const response = await axios.put(url, param);

        dispatch(doUpdateProfileSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateProfileFail(error.response));
    }
};

export const doResetUpdateProfile = () => async (dispatch) => {
    dispatch(resetUpdateProfile());
};

export const doUpdatePassword = (param) => async (dispatch) => {
    try {
        dispatch(doUpdatePasswordLoading());
        const url = `/api/customer/auth/password`;
        const response = await axios.put(url, param);

        dispatch(doUpdatePasswordSuccess(response.data));
    } catch (error) {
        dispatch(doUpdatePasswordFail(error.response));
    }
};

export const doResetUpdatePassword = () => async (dispatch) => {
    dispatch(resetUpdatePassword());
};

// Refresh Token API Call
export const doRefreshToken = () => async (dispatch, getState) => {
    try {
        const state = getState();
        const refreshToken = state.core?.refreshToken;

        if (!refreshToken) {
            dispatch(doRefreshTokenFail());
            return;
        }

        dispatch(setRefreshing(true));

        const url = `/api/customer/auth/refresh-token`;
        const response = await axios.post(url, {
            refresh_token: refreshToken
        });

        // Assuming response contains accessToken and optionally refreshToken
        const { accessToken, refreshToken: newRefreshToken } = response.data.data || response.data;

        dispatch(doRefreshTokenSuccess({
            accessToken,
            refreshToken: newRefreshToken || refreshToken
        }));

        return response.data;
    } catch (error) {
        dispatch(doRefreshTokenFail());
        throw error;
    }
};

// Helper to store tokens
export const doSetTokens = (tokens) => async (dispatch) => {
    dispatch(setTokens(tokens));
};

// Helper to clear tokens
export const doClearTokens = () => async (dispatch) => {
    dispatch(clearTokens());
};

export const {
    setSideBarShow,
    setCommonData,

    doSendOtpSuccess,
    doSendOtpFail,

    doVerifyOtpSuccess,
    doVerifyOtpFail,

    doRegisterSuccess,
    doRegisterFail,

    doLoginSuccess,
    doLoginFail,

    doLoggedInSuccess,
    doLoggedInFail,

    doLogoutSuccess,
    doLogoutFail,

    doInsertSearchActivityLogSuccess,
    doInsertSearchActivityLogFail,

    setUrlParam,

    resetRegisterState,
    resetLoginFail,
    resetLogoutState,
    resetSendOtp,
    resetVerifyOtp,

    // Refresh Token Actions
    setTokens,
    clearTokens,
    setRefreshing,
    doRefreshTokenSuccess,
    doRefreshTokenFail,

    // Credit Reload Actions
    doProcessCreditReloadSuccess,
    doProcessCreditReloadFail,
    resetProcessCreditReload,

    // Update Profile Actions
    doUpdateProfileSuccess,
    doUpdateProfileFail,
    resetUpdateProfile,

    // Update Password Actions
    doUpdatePasswordLoading,
    doUpdatePasswordSuccess,
    doUpdatePasswordFail,
    resetUpdatePassword,
} = coreSlide.actions;

export const getSideBarShow = (state) => state.core?.sidebarShow;
export const getCommonData = (state) => state.core?.commonData;

export const getRegisterSuccess = (state) => state.core?.registerSuccess;
export const getRegisterFail = (state) => state.core?.registerFail;
export const getRegisterErrorMessage = (state) => state.core?.registerErrorMessage;
export const getRegisterResponseData = (state) => state.core?.registerResponseData;

export const getSendOtpSuccess = (state) => state.core?.sendOtpSuccess;
export const getSendOtpFail = (state) => state.core?.sendOtpFail;
export const getSendOtpResponseData = (state) => state.core?.sendOtpResponseData;
export const getSendOtpErrorMessage = (state) => state.core?.sendOtpErrorMessage;

export const getVerifyOtpSuccess = (state) => state.core?.verifyOtpSuccess;
export const getVerifyOtpFail = (state) => state.core?.verifyOtpFail;
export const getVerifyOtpResponseData = (state) => state.core?.verifyOtpResponseData;
export const getVerifyOtpErrorMessage = (state) => state.core?.verifyOtpErrorMessage;

export const getLoginSuccess = (state) => state.core?.loginSuccess;
export const getLoginFail = (state) => state.core?.loginFail;
export const getLoginFailedDetail = (state) => state.core?.loginFailedDetail;

export const getLoggedIn = (state) => state.core?.loggedIn;
export const getLogoutSuccess = (state) => state.core?.logoutSuccess;

export const getUrlParam = (state) => state.core?.urlParam;

// Refresh Token Selectors
export const getAccessToken = (state) => state.core?.accessToken;
export const getRefreshToken = (state) => state.core?.refreshToken;
export const getIsRefreshing = (state) => state.core?.isRefreshing;
export const getRefreshTokenSuccess = (state) => state.core?.refreshTokenSuccess;
export const getRefreshTokenFail = (state) => state.core?.refreshTokenFail;

// Credit Reload Selectors
export const getProcessCreditReloadSuccess = (state) => state.core?.processCreditReloadSuccess;
export const getProcessCreditReloadFail = (state) => state.core?.processCreditReloadFail;
export const getProcessCreditReloadErrorMessage = (state) => state.core?.processCreditReloadErrorMessage;
export const getProcessCreditReloadResponseData = (state) => state.core?.processCreditReloadResponseData;

// Update Profile Selectors
export const getUpdateProfileSuccess = (state) => state.core?.updateProfileSuccess;
export const getUpdateProfileFail = (state) => state.core?.updateProfileFail;
export const getUpdateProfileErrorMessage = (state) => state.core?.updateProfileErrorMessage;

// Update Password Selectors
export const getUpdatePasswordLoading = (state) => state.core?.updatePasswordLoading;
export const getUpdatePasswordSuccess = (state) => state.core?.updatePasswordSuccess;
export const getUpdatePasswordFail = (state) => state.core?.updatePasswordFail;
export const getUpdatePasswordErrorMessage = (state) => state.core?.updatePasswordErrorMessage;

export default coreSlide.reducer;
